/**
 * Wavefront OBJ + minimal MTL export (geometry, normals, UVs when present).
 * Companion .mtl uses diffuse color from the mesh material (no texture files).
 */

import * as THREE from 'three';
import { OBJExporter } from 'three/examples/jsm/exporters/OBJExporter.js';

export const OBJ_EXPORT_MTL_NAME = 'socket_export';
export const OBJ_EXPORT_MATERIAL_NAME = 'socket_export';

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function triggerDownloadUrl(url, filename) {
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

function pickMaterial(mesh) {
  const m = mesh.material;
  if (!m) return null;
  return Array.isArray(m) ? m[0] : m;
}

/**
 * Minimal MTL: diffuse only (matches typical scan preview materials).
 */
export function materialToMtlString(
  material,
  newmtlName = OBJ_EXPORT_MATERIAL_NAME,
  textureFileName = null
) {
  const c = material?.color ?? new THREE.Color(0xcccccc);
  const lines = [
    `newmtl ${newmtlName}`,
    `Kd ${c.r} ${c.g} ${c.b}`,
    'Ka 0 0 0',
    'Ks 0.2 0.2 0.2',
    'Ns 0',
    ''
  ];
  if (textureFileName) {
    lines.splice(2, 0, `map_Ka ${textureFileName}`, `map_Kd ${textureFileName}`);
  }
  return lines.join('\n');
}

/**
 * @param {THREE.Mesh} mesh
 * @param {string} stem - filename stem without extension (shared by .obj and .mtl)
 * @param {{ textureFileName?: string|null }} [options]
 * @returns {{ objText: string, mtlText: string, objFile: string, mtlFile: string }}
 */
export function meshToOBJExport(mesh, stem, options = {}) {
  if (!mesh?.geometry) {
    throw new Error('Invalid mesh provided for OBJ export');
  }

  const { textureFileName = null } = options;
  const mtlFile = `${stem}.mtl`;
  const objFile = `${stem}.obj`;

  const exportMesh = mesh.clone(true);
  exportMesh.updateMatrixWorld(true);

  const mat = pickMaterial(exportMesh);
  if (mat) {
    mat.name = OBJ_EXPORT_MATERIAL_NAME;
  }

  const exporter = new OBJExporter();
  const objBody = exporter.parse(exportMesh);

  const mtlText = materialToMtlString(mat, OBJ_EXPORT_MATERIAL_NAME, textureFileName);
  const objText = `mtllib ${mtlFile}\n${objBody}`;

  return { objText, mtlText, objFile, mtlFile };
}

/**
 * Download .obj then .mtl (short delay so browsers accept both).
 */
export function downloadOBJPackage(mesh, stem) {
  const { objText, mtlText, objFile, mtlFile } = meshToOBJExport(mesh, stem);
  const objBlob = new Blob([objText], { type: 'model/obj' });
  const mtlBlob = new Blob([mtlText], { type: 'text/plain' });
  triggerDownload(objBlob, objFile);
  window.setTimeout(() => {
    triggerDownload(mtlBlob, mtlFile);
  }, 400);
}

/** After saving .obj via File System Access API, downloads the matching .mtl. */
export function downloadMtlCompanion(mesh, stem) {
  const { mtlText, mtlFile } = meshToOBJExport(mesh, stem);
  triggerDownload(new Blob([mtlText], { type: 'text/plain' }), mtlFile);
}

/**
 * Download the originally-uploaded OBJ package (OBJ + optional MTL + textures) from blob URLs.
 * This preserves the MTL's texture filenames (e.g. `map_Kd ManojBk_0.jpg`).
 *
 * @param {{ objUrl: string, mtlUrl?: string|null, textureUrls?: Array<{name: string, url: string}>, objName?: string, mtlName?: string|null }} objData
 */
export function downloadOBJUploadedPackage(objData) {
  if (!objData?.objUrl) throw new Error('Missing objUrl');
  const objName = objData.objName || 'socket.obj';
  triggerDownloadUrl(objData.objUrl, objName);

  if (objData.mtlUrl) {
    const mtlName = objData.mtlName || objName.replace(/\.obj$/i, '.mtl');
    window.setTimeout(() => triggerDownloadUrl(objData.mtlUrl, mtlName), 250);
  }

  const textures = Array.isArray(objData.textureUrls) ? objData.textureUrls : [];
  textures.forEach((t, i) => {
    if (!t?.url) return;
    const name = t?.name || `texture_${i}.jpg`;
    window.setTimeout(() => triggerDownloadUrl(t.url, name), 350 + i * 120);
  });
}
